import React, { useContext, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/authContext";
import { useTheme } from "../context/themeContext";
import logo from "../components/images/logo2026.png";
import MobileMenu from "./MobileMenu";
import { motion, AnimatePresence } from "framer-motion";
import { FiClock, FiAlertTriangle, FiMoon, FiSun } from "react-icons/fi";

const Navigation = () => {
  const { auth, handleLogout, sessionWarning, sessionTimeLeft, extendSession } =
    useContext(AuthContext);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      setCurrentTime(timeString);
    };

    // Display only shows hours:minutes, so a 30s tick is plenty
    const timer = setInterval(updateTime, 30000);
    updateTime(); // Set initial time

    return () => clearInterval(timer); // Cleanup interval on component unmount
  }, []);

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/projects", label: "Projects" },
    { path: "/blogs", label: "Blogs" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  const handleLogoutClick = () => {
    handleLogout();
    if (process.env.NODE_ENV === "production") {
      window.location.replace("/");
    }
  };

  return (
    <>
      {/* Session Warning Banner */}
      <AnimatePresence>
        {sessionWarning && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-gradient-to-r from-yellow-600 to-orange-600 text-white px-6 py-3 flex items-center justify-between shadow-lg"
          >
            <div className="flex items-center space-x-3">
              <FiAlertTriangle className="text-xl flex-shrink-0" />
              <div>
                <p className="font-semibold">Session Expiring Soon</p>
                <p className="text-sm">
                  Your session will expire in {sessionTimeLeft} minute
                  {sessionTimeLeft !== 1 ? "s" : ""}. Save your work or you'll
                  be automatically logged out.
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <button
                onClick={extendSession}
                className="bg-white/20 hover:bg-white/30 px-4 py-1 rounded text-sm font-medium transition-colors"
              >
                Dismiss
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <nav className="relative z-20 p-6 lg:p-8 flex items-center justify-between border-b border-gray-200/80 dark:border-brand-purple/10 bg-white/75 text-gray-800 backdrop-blur-md dark:bg-brand-dark/75 dark:text-brand-cream">
        {/* Left Section - Logo and Title */}
        <div className="flex items-center">
          <span className="text-gray-800 dark:text-brand-cream font-bold text-xl lg:text-2xl mr-3">
            &#123; David Seibold &#125;
          </span>
          <img
            src={logo}
            alt="David Seibold's personal logo"
            className="w-8 h-8 lg:w-10 lg:h-10 rounded-full"
          />
        </div>

        {/* Desktop Navigation - Hidden on mobile */}
        <ul className="hidden lg:flex items-center space-x-8">
          {navItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                aria-current={location.pathname === item.path ? "page" : undefined}
                className={`relative px-3 py-2 text-lg font-medium transition-all duration-300 ${
                  location.pathname === item.path
                    ? "text-brand-gold"
                    : "text-gray-800 dark:text-brand-cream hover:text-brand-gold"
                }`}
              >
                {item.label}
                {location.pathname === item.path && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-gold to-brand-purple"
                    layoutId="activeNavIndicator"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </Link>
            </li>
          ))}

          {/* Auth Links for Desktop */}
          {!auth.isAuthenticated ? (
            <li>
              <Link
                to="/login"
                className={`relative px-3 py-2 text-lg font-medium transition-colors duration-300 ${
                  location.pathname === "/login"
                    ? "text-brand-gold"
                    : "text-gray-800 dark:text-brand-cream hover:text-brand-gold"
                } group`}
                title="Admin Access"
              >
                Admin
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-gradient-to-r from-brand-gold to-brand-purple rounded-full opacity-60 group-hover:opacity-100 transition-opacity"></span>
                {location.pathname === "/login" && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-gold to-brand-purple"
                    layoutId="activeNavIndicator"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </Link>
            </li>
          ) : (
            <>
              <li>
                <Link
                  to="/admin-dashboard"
                  className={`relative px-3 py-2 text-lg font-medium transition-colors duration-300 ${
                    location.pathname === "/admin-dashboard"
                      ? "text-brand-gold"
                      : "text-gray-800 dark:text-brand-cream hover:text-brand-gold"
                  } group`}
                >
                  Dashboard
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  {location.pathname === "/admin-dashboard" && (
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-gold to-brand-purple"
                      layoutId="activeNavIndicator"
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                    />
                  )}
                </Link>
              </li>
              <li>
                <button
                  onClick={handleLogoutClick}
                  className="px-3 py-2 text-lg font-medium text-red-400 hover:text-red-300 transition-colors duration-300 relative group"
                  title="Sign out of admin area"
                >
                  Logout
                  <span className="absolute inset-x-0 -bottom-0.5 h-0.5 bg-red-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></span>
                </button>
              </li>
            </>
          )}
        </ul>

        {/* Right Section - City, Clock and Mobile Menu Button */}
        <div className="flex items-center space-x-4">
          <button
            onClick={toggleTheme}
            className="hidden lg:inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-gray-700 dark:text-brand-cream hover:text-brand-gold dark:hover:text-brand-gold border border-gray-300 dark:border-brand-purple/20 hover:border-brand-gold/40 transition-colors"
            aria-label="Toggle theme"
            title="Toggle theme"
          >
            {theme === "dark" ? <FiSun /> : <FiMoon />}
            {theme === "dark" ? "Light" : "Dark"}
          </button>
          {/* City and Clock - Hidden on mobile */}
          <div className="hidden lg:block text-gray-800 dark:text-brand-cream text-right">
            <p className="font-semibold text-lg">San Diego, CA</p>
            <p className="text-2xl text-lime-600 dark:text-lime-400 font-mono flex items-center justify-end">
              <FiClock className="mr-2" /> {currentTime}
            </p>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden p-2 text-gray-800 dark:text-brand-cream hover:text-brand-gold transition-colors duration-300"
            aria-label="Open mobile menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />
    </>
  );
};

export default Navigation;
